/**
 * Shared title hygiene for every page template.
 *
 * Two rules, applied in this order:
 *  1. No price may ever appear in a <title> (transient or final). Prices in page
 *     BODY copy are untouched — this only sanitizes the document title.
 *  2. Titles must stay <= 60 characters, trimmed from the END on a word boundary
 *     so the descriptive part of the title survives (never reduced to the brand).
 */

/** "from €5.50/hr", "dès 5,50€/h", "ab 5,50 €/Std", "من 5,50 €/ساعة", "€5.50/u"… */
const PRICE_PHRASE =
  /(?:\b(?:from|starting(?:\s+at)?|only|d\u00e8s|des|\u00e0 partir de|a partir de|desde|ab|da|od|vanaf|fra|alkaen|dari|mula|\u03b1\u03c0\u03cc|de la|von|\u0645\u0646|\u0628\u062f\u0621\u0627\u064b \u0645\u0646)\s*)?(?:[\u20ac$\u00a3]\s?\d+(?:[.]\d+)?|\d+(?:[.]\d+)?\s?(?:\u20ac|\$|\u00a3|EUR|USD|MAD|AED))\s*\/\s*[^\s|\u2013\u2014,;]+|\b(?:from|starting(?:\s+at)?|only|d\u00e8s|des|\u00e0 partir de|a partir de|desde|ab|da|od|vanaf|fra|alkaen|dari|mula|\u03b1\u03c0\u03cc|de la|von|\u0645\u0646|\u0628\u062f\u0621\u0627\u064b \u0645\u0646)\s*\d+(?:[.]\d+)?\s*\/\s*[^\s|\u2013\u2014,;]+/gi;

export function stripPriceFromTitle(value: string): string {
  let out = (value || '').replace(PRICE_PHRASE, '');
  // Clean separators left dangling by the removal.
  out = out
    .replace(/\s*[|–—-]\s*(?=[|–—-])/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/[\s,;·|–—-]+$/g, '')
    .replace(/^[\s,;·|–—-]+/g, '')
    .trim();
  out = out
    .replace(/\s+(?:from|starting|only|d\u00e8s|desde|ab|da|od|vanaf|fra|alkaen|dari|mula|de la|von|\u0645\u0646|\u0627\u0628\u062a\u062f\u0627\u0621\u064b \u0645\u0646)$/i, '')
    .replace(/[\s,;\u00b7|\u2013\u2014-]+$/g, '')
    .trim();
  return out;
}

export function clampTitleLength(value: string, max = 60): string {
  const v = (value || '').trim();
  if (v.length <= max) return v;
  // Prefer dropping whole trailing segments (| or — delimited) so the title never
  // ends mid-phrase; only word-trim when the first segment alone is too long.
  // First, shed trailing comma clauses (", 24/7") — cheaper than losing a whole segment.
  let base = v;
  while (base.length > max && base.includes(',')) {
    base = base.slice(0, base.lastIndexOf(',')).trim();
  }
  if (base.length <= max && base.length >= max * 0.5) return base;
  const parts = v.split(/\s*(?:\||–|—)\s*/).filter(Boolean);
  if (parts.length > 1) {
    for (let end = parts.length - 1; end >= 1; end--) {
      const candidate = parts.slice(0, end).join(' | ');
      if (candidate.length <= max) return candidate;
    }
  }
  const first = parts[0] || v;
  const cut = first.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  const trimmed = lastSpace > max * 0.5 ? cut.slice(0, lastSpace) : cut;
  return trimmed.replace(/[\s,;·|–—-]+$/g, '');
}

/** Price-free, <= 60 characters. */
export function sanitizeTitle(value: string, max = 60): string {
  return clampTitleLength(stripPriceFromTitle(value), max);
}
